// Context para gerenciar projeto ativo
import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '@/services/api';
import { cronogramaRepository } from '@/storage/cronogramaRepository';

export interface Projeto {
  id: string;
  nome: string;
  descricao?: string;
  ativo: boolean;
  empresaId?: string | null;
  squadId?: string | null;
  squad?: { id: string; nome: string; regiao?: string | null } | null;
  clienteTipo?: 'arcelor' | 'praxis' | 'ar' | string | null;
  configuracaoDocumento?: any;
  tipoProjeto?: 'padrao' | 'budget' | 'manual_operacao' | string;
  budgetHabilitado?: boolean;
  budgetTotal?: number | null;
  createdAt: string;
  updatedAt: string;
  contadores?: {
    apreciacoes: number;
    templates: number;
    ativos: number;
    maquinas: number;
  };
}

export interface ProjetoExtras {
  squadId?: string | null;
  tipoProjeto?: 'padrao' | 'budget' | 'manual_operacao' | string;
  budgetHabilitado?: boolean;
  budgetTotal?: number | null;
}

export interface ProjetoContextType {
  projeto: Projeto | null;
  projetos: Projeto[];
  loading: boolean;
  stale: boolean;
  lastSyncAt?: string;
  selecionarProjeto: (projeto: Projeto) => void;
  carregarProjetos: () => Promise<void>;
  criarProjeto: (nome: string, descricao?: string, clienteTipo?: Projeto['clienteTipo'], extras?: ProjetoExtras) => Promise<{ success: boolean; message?: string; data?: Projeto }>;
  atualizarProjeto: (id: string, nome: string, descricao?: string, clienteTipo?: Projeto['clienteTipo'], extras?: ProjetoExtras) => Promise<{ success: boolean; message?: string }>;
  removerProjeto: (id: string) => Promise<{ success: boolean; message?: string }>;
  projetoSelecionado: boolean;
}

export const ProjetoContext = createContext<ProjetoContextType | undefined>(undefined);

const PROJETO_ATIVO_KEY = 'projetoAtivo';
const PROJETOS_CACHE_KEY = 'projetosCache';
const PROJETOS_CACHE_UPDATED_AT_KEY = 'projetosCacheAtualizadoEm';

export function ProjetoProvider({ children }: { children: ReactNode }) {
  const [projeto, setProjeto] = useState<Projeto | null>(null);
  const [projetos, setProjetos] = useState<Projeto[]>([]);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | undefined>(undefined);

  const isConnectivityFailure = (message?: string, errors?: unknown[]) => {
    const base = (message || '').toLowerCase();
    if (base.includes('conectar') || base.includes('network')) return true;
    return (errors || []).some((item) => String(item).toLowerCase().includes('network'));
  };

  const isAuthFailure = (message?: string, errors?: unknown[]) => {
    const base = (message || '').toLowerCase();
    if (base.includes('não autorizado') || base.includes('nao autorizado') || base.includes('token')) return true;
    return (errors || []).some((item) => {
      const normalized = String(item).toLowerCase();
      return normalized.includes('não autorizado') || normalized.includes('nao autorizado') || normalized.includes('token');
    });
  };

  // Carregar projeto selecionado do localStorage ao iniciar
  useEffect(() => {
    const loadProjeto = async () => {
      const storedProjeto = localStorage.getItem(PROJETO_ATIVO_KEY);
      const storedProjetos = localStorage.getItem(PROJETOS_CACHE_KEY);
      const cacheUpdatedAt = localStorage.getItem(PROJETOS_CACHE_UPDATED_AT_KEY);

      if (cacheUpdatedAt) {
        setLastSyncAt(cacheUpdatedAt);
      }

      if (storedProjetos) {
        try {
          const parsedProjetos = JSON.parse(storedProjetos);
          if (Array.isArray(parsedProjetos)) {
            setProjetos(parsedProjetos);
          }
        } catch (error) {
          console.error('Erro ao carregar cache de projetos:', error);
        }
      }

      if (storedProjeto) {
        try {
          const projetoParsed = JSON.parse(storedProjeto);
          setProjeto(projetoParsed);

          if (navigator.onLine) {
            const response = await apiService.buscarProjeto(projetoParsed.id);
            if (response.success && response.data) {
              setProjeto(response.data);
              setStale(false);
              localStorage.setItem(PROJETO_ATIVO_KEY, JSON.stringify(response.data));
            } else {
              if (isConnectivityFailure(response.message, response.errors) || isAuthFailure(response.message, response.errors)) {
                setStale(true);
              } else {
                console.warn('Projeto do localStorage não existe mais no banco de dados');
                localStorage.removeItem(PROJETO_ATIVO_KEY);
                setProjeto(null);
              }
            }
          } else {
            setStale(true);
          }
        } catch (error) {
          console.error('Erro ao carregar projeto do localStorage:', error);
          localStorage.removeItem(PROJETO_ATIVO_KEY);
          setProjeto(null);
        }
      }
      setLoading(false);
    };

    loadProjeto();
  }, []);

  const carregarProjetos = useCallback(async () => {
    try {
      const response = await apiService.listarProjetos({ ativo: true });
      if (response.success && response.data) {
        const lista = Array.isArray(response.data) ? response.data : [];
        setProjetos(lista);
        setStale(false);
        const syncAt = new Date().toISOString();
        setLastSyncAt(syncAt);
        localStorage.setItem(PROJETOS_CACHE_KEY, JSON.stringify(lista));
        localStorage.setItem(PROJETOS_CACHE_UPDATED_AT_KEY, syncAt);

        if (projeto) {
          const atualizado = lista.find((item) => item.id === projeto.id) || null;
          if (atualizado) {
            setProjeto(atualizado);
            localStorage.setItem(PROJETO_ATIVO_KEY, JSON.stringify(atualizado));
          } else {
            setProjeto(null);
            localStorage.removeItem(PROJETO_ATIVO_KEY);
          }
        }
      } else if (
        isConnectivityFailure(response.message, response.errors)
        || isAuthFailure(response.message, response.errors)
        || !navigator.onLine
      ) {
        setStale(true);
      }
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
      if (!navigator.onLine) {
        setStale(true);
      }
    }
  }, [projeto]);

  useEffect(() => {
    if (!projeto?.id || !navigator.onLine) return;

    let cancelled = false;
    const preloadCronograma = async () => {
      try {
        const response = await apiService.listarCronograma({ projetoId: projeto.id });
        if (cancelled || !response.success || !response.data) return;
        await cronogramaRepository.saveTasks(projeto.id, response.data.tarefas);
      } catch (error) {
        console.warn('Falha ao pré-carregar cronograma para uso offline:', error);
      }
    };

    preloadCronograma();
    return () => {
      cancelled = true;
    };
  }, [projeto?.id]);

  const selecionarProjeto = (projetoSelecionado: Projeto) => {
    setProjeto(projetoSelecionado);
    localStorage.setItem(PROJETO_ATIVO_KEY, JSON.stringify(projetoSelecionado));
  };

  const criarProjeto = async (nome: string, descricao?: string, clienteTipo: Projeto['clienteTipo'] = 'arcelor', extras?: ProjetoExtras) => {
    try {
      const response = await apiService.criarProjeto({ nome, descricao, clienteTipo, ...extras });
      if (response.success && response.data) {
        await carregarProjetos();
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          message: response.message || 'Erro ao criar projeto'
        };
      }
    } catch (error) {
      console.error('Erro ao criar projeto:', error);
      return {
        success: false,
        message: 'Erro ao conectar com o servidor'
      };
    }
  };

  const atualizarProjeto = async (id: string, nome: string, descricao?: string, clienteTipo?: Projeto['clienteTipo'], extras?: ProjetoExtras) => {
    try {
      const response = await apiService.atualizarProjeto(id, { nome, descricao, clienteTipo, ...extras });
      if (response.success) {
        await carregarProjetos();
        // Se o projeto atualizado é o projeto ativo, atualizar também
        if (projeto?.id === id) {
          const projetoAtualizado = projetos.find(p => p.id === id);
          if (projetoAtualizado) {
            selecionarProjeto({ ...projetoAtualizado, nome, descricao, clienteTipo: clienteTipo !== undefined ? (clienteTipo || null) : projetoAtualizado.clienteTipo, ...extras });
          }
        }
        return { success: true };
      } else {
        return {
          success: false,
          message: response.message || 'Erro ao atualizar projeto'
        };
      }
    } catch (error) {
      console.error('Erro ao atualizar projeto:', error);
      return {
        success: false,
        message: 'Erro ao conectar com o servidor'
      };
    }
  };

  const removerProjeto = async (id: string) => {
    try {
      const response = await apiService.removerProjeto(id);
      if (response.success) {
        await carregarProjetos();
        // Se o projeto removido é o projeto ativo, limpar seleção
        if (projeto?.id === id) {
          setProjeto(null);
          localStorage.removeItem(PROJETO_ATIVO_KEY);
        }
        return { success: true };
      } else {
        return {
          success: false,
          message: response.message || 'Erro ao remover projeto'
        };
      }
    } catch (error) {
      console.error('Erro ao remover projeto:', error);
      return {
        success: false,
        message: 'Erro ao conectar com o servidor'
      };
    }
  };

  const value: ProjetoContextType = {
    projeto,
    projetos,
    loading,
    stale,
    lastSyncAt,
    selecionarProjeto,
    carregarProjetos,
    criarProjeto,
    atualizarProjeto,
    removerProjeto,
    projetoSelecionado: !!projeto
  };

  return <ProjetoContext.Provider value={value}>{children}</ProjetoContext.Provider>;
}
